import { createSlice, current } from '@reduxjs/toolkit'
import filter from 'lodash/filter'
import groupBy from 'lodash/groupBy'
import keyBy from 'lodash/keyBy'
import mapValues from 'lodash/mapValues'

import {
  fetchLocales,
  fetchLocationsMeta,
  searchDetailedTargeting,
  searchLocations,
} from 'apps/ads/actions/targetingActionsTyped'
import { SearchTargetResponse } from 'shared/api/requests/ads/schema'

type AdSetTargetingItem = SearchTargetResponse['data'][number]

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isTargetingItem = (value: unknown): value is AdSetTargetingItem => isRecord(value)

const toItemsArray = (value: unknown): AdSetTargetingItem[] => {
  if (Array.isArray(value)) {
    return value
  }
  if (isRecord(value)) {
    return Object.values(value).filter(isTargetingItem)
  }
  return []
}

interface TargetingState {
  data: Record<string, Record<string, unknown>>
  search: Record<string, Array<[string | number | null | undefined, string | undefined]>>
  locales: Record<string, unknown>
  detailedTargetingData: Record<string, unknown>
  detailedTargetingSearch: Record<string, Array<string | number | undefined>>
}

const initialState: TargetingState = {
  data: {
    country: {},
    region: {},
    city: {},
    zip: {},
    place: {},
    custom_location: {},
    geo_market: {},
    electoral_district: {},
    country_group: {},
  },
  search: {},
  locales: {},
  detailedTargetingData: {},
  detailedTargetingSearch: {},
}

const targetingSlice = createSlice({
  name: 'adsTargeting',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(searchLocations.fulfilled, (state, action) => {
        const items = filter(
          action.payload.data,
          (item) => item.type != null && Object.keys(state.data).includes(item.type),
        )
        const byType = groupBy(items, 'type')
        const currentData = current(state).data
        state.data = mapValues(currentData, (itemsByType, type) => ({
          ...itemsByType,
          ...keyBy(byType[type] ?? [], 'key'),
        }))
        state.search[action.meta.arg ?? ''] = items.map((item) => [item.key, item.type])
      })
      .addCase(fetchLocationsMeta.fulfilled, (state, action) => {
        const metaGroup = action.payload.data[0]
        const byType: Record<string, AdSetTargetingItem[]> = {}

        for (const value of Object.values(metaGroup ?? {})) {
          const items = toItemsArray(value)
          const [first] = items
          if (!first?.type) {
            continue
          }
          byType[first.type] = items
        }

        const currentData = current(state).data
        state.data = mapValues(currentData, (itemsByType, type) => ({
          ...itemsByType,
          ...keyBy(byType[type] ?? [], 'key'),
        }))
      })
      .addCase(fetchLocales.fulfilled, (state, action) => {
        state.locales = keyBy(action.payload.data, 'key')
      })
      .addCase(searchDetailedTargeting.fulfilled, (state, action) => {
        const detailedTargetingData = keyBy(action.payload.data, 'id')
        state.detailedTargetingData = {
          ...state.detailedTargetingData,
          ...detailedTargetingData,
        }
        state.detailedTargetingSearch[action.meta.arg.query ?? ''] = action.payload.data.map(
          (item) => item.id,
        )
      })
  },
})

export const targetingReducer = targetingSlice.reducer
